import { Link } from '@mui/material'
import { COLOR_PRIMARY } from '../theme'
import type { PolicyContent } from '../components/PolicyRenderer'

export const cookieContent: PolicyContent = {
    title: 'Cookie Policy',
    lastUpdated: '3 October 2021',

    intro: [
        {
            type: 'para',
            content: (
                <>
                    Please read this cookie policy carefully as it contains important information on who we are and how we use cookies on our website. This policy should be read together with our{' '}
                    <Link href="/privacy" sx={{ color: COLOR_PRIMARY }}>Privacy Policy</Link>
                    {' '}which sets out how and why we collect, store, use and share personal information generally, as well as your rights in relation to your personal information and details of how to contact us and supervisory authorities if you have a complaint.
                </>
            ),
        },
    ],

    sections: [
        {
            title: 'Who We Are',
            blocks: [
                {
                    type: 'para',
                    content: (
                        <>
                            This website{' '}
                            <Link href="https://www.contractfinderpro.com/" target="_blank" rel="noopener noreferrer" sx={{ color: COLOR_PRIMARY }}>
                                https://www.contractfinderpro.com/
                            </Link>
                            {' '}is operated by the J4L Group Limited (company number 11012403) trading as Contract Finder Pro ("CFP", "we" and "us"). We are a private company limited by shares and registered in England and Wales.
                        </>
                    ),
                },
                {
                    type: 'para',
                    content: (
                        <>
                            This cookie policy only relates to your use of our website,{' '}
                            <Link href="https://www.contractfinderpro.com/" target="_blank" rel="noopener noreferrer" sx={{ color: COLOR_PRIMARY }}>
                                https://www.contractfinderpro.com/
                            </Link>.
                        </>
                    ),
                },
                { type: 'para', content: 'Throughout our website we may link to other websites owned and operated by certain trusted third parties to, for example, make additional products and services available to you (such as payment and newsletter services). These other third-party websites may also use cookies or similar technologies in accordance with their own separate policies. For privacy information relating to these other third-party websites, please consult their policies as appropriate.' },
            ],
        },

        {
            title: 'Cookies',
            blocks: [
                { type: 'para', content: 'A cookie is a small text file which is placed onto your device (eg computer, smartphone or other electronic device) when you use our website. We use cookies on our website. These help us to recognise you and your device and store some information about your preferences or past actions.' },
                { type: 'para', content: 'For example, we may monitor how many times you visit the website, which pages you go to, traffic data, location data and the originating domain name of your internet service provider. This information helps us to build a profile of our users. Some of this data will be aggregated or statistical, which means that we will not be able to identify you individually.' },
                {
                    type: 'para',
                    content: (
                        <>
                            For further information on cookies generally, including how to control and manage them, visit the guidance on cookies published by the UK Information Commissioner's Office,{' '}
                            <Link href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" sx={{ color: COLOR_PRIMARY }}>www.aboutcookies.org</Link>
                            {' '}or{' '}
                            <Link href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" sx={{ color: COLOR_PRIMARY }}>www.allaboutcookies.org</Link>.
                        </>
                    ),
                },
            ],
        },

        {
            title: 'Consent to Use Cookies and Changing Settings',
            blocks: [
                { type: 'para', content: 'A cookie is a small file of letters and numbers that we store on your browser or the hard drive of your computer if you agree. Cookies contain information that is transferred to your computer\'s hard drive.' },
                { type: 'para', content: 'We use the following cookies:' },
                {
                    type: 'list',
                    items: [
                        <><strong>Strictly necessary cookies.</strong> These are cookies that are required for the operation of our website. They include, for example, cookies that enable you to log into secure areas of our website, use a shopping cart or make use of e-billing services.</>,
                        <><strong>Analytical/performance cookies.</strong> They allow us to recognise and count the number of visitors and to see how visitors move around our website when they are using it. This helps us to improve the way our website works, for example, by ensuring that users are finding what they are looking for easily.</>,
                        <><strong>Functionality cookies.</strong> These are used to recognise you when you return to our website. This enables us to personalise our content for you, greet you by name and remember your preferences (for example, your choice of language or region).</>,
                        <><strong>Targeting cookies.</strong> These cookies record your visit to our website, the pages you have visited and the links you have followed. We will use this information to make our website and the advertising displayed on it more relevant to your interests. We may also share this information with third parties for this purpose.</>,
                    ],
                },
                { type: 'para', content: 'You can find more information about the individual cookies we use and the purposes for which we use them in the table below:' },
                {
                    type: 'table',
                    headers: ['Name', 'Purpose', 'Essential / Consent required'],
                    rows: [
                        ['ASP.NET_SessionId', 'Stores user authentication & session', 'Essential'],
                        ['cookiebar', 'Stores cookie acceptance preference', 'Essential'],
                        ['__stripe_mid', 'Used by Stripe for subscription payments', 'Essential'],
                        ['SL_C_*', 'Used by Smartlook to optimize user experience on the platform', 'We seek consent'],
                        ['me', 'Used to tailor user experience to your region', 'We seek consent'],
                        ['_gid', 'Used by Google Analytics', 'We seek consent'],
                        ['_ga', 'Used by Google Analytics', 'We seek consent'],
                        ['_gcl_au', 'Used by Google AdSense', 'We seek consent'],
                    ],
                },
                { type: 'para', content: 'We will ask for your consent to place cookies or other similar technologies on your device, except where they are essential for us to provide you with a service that you have requested. Please note, that by continuing to browse the site, you are agreeing to our use of cookies.' },
                {
                    type: 'para',
                    content: (
                        <>
                            You can withdraw any consent to the use of cookies or manage any other cookie preferences by visiting{' '}
                            <Link href="/cookies" sx={{ color: COLOR_PRIMARY }}>
                                our Cookie Policy page
                            </Link>
                        </>
                    ),
                },
            ],
        },

        {
            title: 'Third Party Access to the Cookies',
            blocks: [
                { type: 'para', content: 'The cookies we use will only be accessed by us and those third parties named in the table above for the purposes referred to in this cookie policy. Those cookies will not be accessed by any other third party.' },
            ],
        },

        {
            title: 'How to Turn Off All Cookies and Consequences of Doing So',
            blocks: [
                { type: 'para', content: 'If you do not want to accept any cookies, you may be able to change your browser settings so that cookies (including those which are essential to the services requested) are not accepted. If you do this, please be aware that you may lose some of the functionality of our website.' },
                { type: 'para', content: 'You block cookies by activating the setting on your browser that allows you to refuse the setting of all or some cookies. However, if you use your browser settings to block all cookies (including essential cookies) you may not be able to access all or parts of our site.' },
                { type: 'para', content: 'Except for essential cookies, all cookies will expire after 2 years.' },
                {
                    type: 'para',
                    content: (
                        <>
                            For further information about cookies and how to disable them please go to the guidance on cookies published by the UK Information Commissioner's Office,{' '}
                            <Link href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" sx={{ color: COLOR_PRIMARY }}>www.aboutcookies.org</Link>
                            {' '}or{' '}
                            <Link href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" sx={{ color: COLOR_PRIMARY }}>www.allaboutcookies.org</Link>.
                        </>
                    ),
                },
            ],
        },

        {
            title: 'How to Contact Us',
            blocks: [
                { type: 'para', content: 'Please contact us if you have any questions about this cookie policy or the information we hold about you.' },
                {
                    type: 'para',
                    content: (
                        <>
                            If you wish to contact us, please send an email to{' '}
                            <Link href="mailto:sales@contractfinderpro.com" sx={{ color: COLOR_PRIMARY }}>
                                sales@contractfinderpro.com
                            </Link>
                            , write to J4L Group Limited, 395 Lugtrout Lane, Catherine-De-Barnes, Solihull, England, B91 2TN or call{' '}
                            <Link href="tel:03337720250" sx={{ color: COLOR_PRIMARY }}>0333 772 0250</Link>.
                        </>
                    ),
                },
            ],
        },

        {
            title: 'Do You Need Extra Help?',
            blocks: [
                { type: 'para', content: 'If you would like this notice in another format (for example: audio, large print, braille) please contact us (see \'How to contact us\' above).' },
            ],
        },

        {
            title: 'Changes to This Policy',
            blocks: [
                { type: 'para', content: 'This policy was published on 4/11/2021 and last updated on 3/10/2021.' },
                { type: 'para', content: 'We may change this policy from time to time, when we do we will inform you via email.' },
                { type: 'para', content: 'The list above is accurate as of the date of publishing, however as we interact with a range of third-party websites outside of our control it is possible that changes to those sites could affect the behaviour of cookies and that changes could occur that we may not be aware of. We will endeavour to update this list regularly but cannot guarantee its accuracy at all times.' },
            ],
        },
    ],
}
